import { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { API_BASE } from "../config";

const COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#06B6D4", "#F97316", "#84CC16", "#EC4899", "#6366F1",
];

export default function Statistics() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [range, setRange] = useState("week");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchStats = useCallback(() => {
    let url = `${API_BASE}/api/statistics`;
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    } else if (range) {
      url += `?range=${range}`;
    }

    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching statistics:", err);
        setLoading(false);
      });
  }, [range, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDateFilter = () => {
    if (!startDate || !endDate) {
      alert("Pilih tanggal mulai dan akhir!");
      return;
    }
    setRange("");
    fetchStats();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setRange("week");
  };

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        📊 Statistik Kunjungan per Ruangan
      </h2>

      {/* Filter controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
        <select
          value={range}
          onChange={(e) => {
            setRange(e.target.value);
            setStartDate("");
            setEndDate("");
          }}
          className="border rounded-lg px-3 py-2"
        >
          <option value="day">Hari ini</option>
          <option value="week">7 Hari Terakhir</option>
          <option value="month">30 Hari Terakhir</option>
        </select>

        <div className="flex items-center gap-2">
          <label className="font-semibold">Dari:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold">Sampai:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <button
          onClick={handleDateFilter}
          className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          Filter
        </button>
        <button
          onClick={handleReset}
          className="bg-gray-300 text-black px-3 py-2 rounded-lg hover:bg-gray-400 transition"
        >
          Reset
        </button>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : stats.length === 0 ? (
          <p className="text-gray-500">Tidak ada data untuk periode ini</p>
        ) : (
          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="lokasi"
                  tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                  labelStyle={{ fontWeight: "bold", color: "#111827" }}
                  formatter={(value) => [`${value} kali`, "Kunjungan"]}
                />
                <Legend />
                <Bar dataKey="visits" radius={[8, 8, 0, 0]}>
                  {stats.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
